import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCopy, FaExternalLinkAlt, FaHeartbeat, FaLink, FaPlus, FaSearch, FaTimes, FaUnlink } from 'react-icons/fa';
import './SchedeCollegate.css';

const clientName = row => [row.cognome, row.nome].filter(Boolean).join(' ') || row.ragione_sociale || 'Cliente non indicato';
const dateOnly = value => value ? new Date(String(value).slice(0, 10) + 'T00:00:00').toLocaleDateString('it-IT') : '';

export default function SchedeCollegate({ schedaId, onCountChange }) {
  const navigate = useNavigate();
  const [linked, setLinked] = useState([]);
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createOptions, setCreateOptions] = useState(null);
  const [createForm, setCreateForm] = useState({ tipo: '', cliente: 'current', condividiSchedaMedica: true });
  const [creating, setCreating] = useState(false);
  const [medicalConflict, setMedicalConflict] = useState(null);

  const refresh = async () => {
    const response = await fetch(`/api/schede-collegate/${schedaId}`);
    if (!response.ok) throw new Error('Collegamenti non disponibili');
    const rows = await response.json();
    setLinked(rows);
    onCountChange?.(rows.length);
  };

  useEffect(() => { if (schedaId) refresh().catch(cause => setError(cause.message));
    // refresh reads only the selected practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedaId]);

  const search = async event => {
    event.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setSearched(false); setError('');
    try {
      const response = await fetch(`/api/schede/search?search=${encodeURIComponent(query.trim())}`);
      if (!response.ok) throw new Error('Ricerca non riuscita');
      const payload = await response.json();
      const rows = Array.isArray(payload) ? payload : payload.items || [];
      const linkedIds = new Set(linked.map(row => String(row.id)));
      setResults(rows.filter(row => String(row.id) !== String(schedaId) && !linkedIds.has(String(row.id))));
      setSearched(true);
    } catch (cause) { setError(cause.message); } finally { setSearching(false); }
  };

  const linkPractice = async otherId => {
    try {
      const response = await fetch(`/api/schede-collegate/${schedaId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ collegata_id: otherId })
      });
      if (!response.ok) throw new Error('Collegamento non riuscito');
      await refresh(); setResults([]); setQuery(''); setSearchOpen(false); setSearched(false); setError('');
    } catch (cause) { setError(cause.message); }
  };

  const unlink = async otherId => {
    if (!window.confirm('Rimuovere il collegamento fra le pratiche?')) return;
    try {
      const response = await fetch(`/api/schede-collegate/${schedaId}/${otherId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Rimozione non riuscita');
      await refresh(); setError('');
    } catch (cause) { setError(cause.message); }
  };

  const openCreate = async () => {
    setError(''); setCreateOpen(true); setCreateOptions(null);
    try {
      const response = await fetch(`/api/schede-collegate/${schedaId}/creation-options`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Dati della pratica non disponibili');
      setCreateOptions(payload);
      setCreateForm({ tipo: '', cliente: 'current', condividiSchedaMedica: true });
    } catch (cause) { setError(cause.message); setCreateOpen(false); }
  };

  const candidates = createOptions ? [createOptions.currentClient,
    ...createOptions.vehicles.flatMap((vehicle, index) => [
      vehicle.proprietario && { value: `vehicle:${index}:proprietario`, label: vehicle.proprietario, detail: `Proprietario · ${vehicle.targa || vehicle.veicolo || `Veicolo ${index + 1}`}` },
      vehicle.conducente && { value: `vehicle:${index}:conducente`, label: vehicle.conducente, detail: `Conducente · ${vehicle.targa || vehicle.veicolo || `Veicolo ${index + 1}`}` }
    ].filter(Boolean))] : [];

  const normalizeName = value => String(value || '').trim().toLocaleLowerCase('it-IT').replace(/\s+/g, ' ');
  const selectedCandidate = candidates.find(item => item.value === createForm.cliente);
  const sameClient = Boolean(createOptions && normalizeName(selectedCandidate?.label) === normalizeName(createOptions.currentClient?.label));

  const changeTargetType = tipo => {
    let suggested = 'current';
    if (tipo === 'Auto') suggested = candidates.find(item => item.value.endsWith(':proprietario'))?.value || 'current';
    if (tipo === 'Conducente') suggested = candidates.find(item => item.value.endsWith(':conducente'))?.value || 'current';
    const suggestedCandidate = candidates.find(item => item.value === suggested);
    const sharesClient = normalizeName(suggestedCandidate?.label) === normalizeName(createOptions?.currentClient?.label);
    setCreateForm(previous => ({ ...previous, tipo, cliente: suggested, condividiSchedaMedica: sharesClient }));
  };

  const shareMedicalPath = async (otherId, sourceId = null) => {
    setError('');
    try {
      const response = await fetch(`/api/schede-collegate/${schedaId}/share-medical`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ collegata_id: otherId, sorgente_id: sourceId })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409 && payload.code === 'MEDICAL_PATH_CONFLICT') {
          setMedicalConflict(linked.find(row => Number(row.id) === Number(otherId)) || { id: otherId });
          return;
        }
        throw new Error(payload.error || 'Condivisione del percorso clinico non riuscita');
      }
      setMedicalConflict(null); await refresh();
    } catch (cause) { setError(cause.message); }
  };

  const createLinkedPractice = async event => {
    event.preventDefault(); setCreating(true); setError('');
    try {
      const endpoint = `/api/schede-collegate/${schedaId}/create`;
      const response = await fetch(endpoint, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createForm)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Creazione della pratica non riuscita');
      await refresh(); setCreateOpen(false);
      navigate(`/pratica/${payload.id}?sezione=riepilogoHome&v=linked1`);
    } catch (cause) { setError(cause.message); } finally { setCreating(false); }
  };

  if (!schedaId) return <p>Salva la pratica prima di collegarne un’altra.</p>;
  return <section className="linked-practices-page">
    <header className="linked-practices-header">
      <div><span>RELAZIONI TRA PRATICHE</span><h1>Schede collegate</h1><p>Collega pratiche relative allo stesso evento o agli stessi soggetti.</p></div>
      <div className="linked-header-actions">
        <button type="button" className="linked-create-button" onClick={openCreate}><FaCopy /> Crea nuova collegata</button>
        <button type="button" onClick={() => { setSearchOpen(value => !value); setResults([]); setSearched(false); setError(''); }}>
          {searchOpen ? <FaTimes /> : <FaPlus />} {searchOpen ? 'Chiudi ricerca' : 'Collega esistente'}
        </button>
      </div>
    </header>

    {searchOpen && <section className="linked-search-card">
      <form onSubmit={search}><div className="linked-search-input"><FaSearch aria-hidden="true" />
        <input autoFocus value={query} onChange={event => { setQuery(event.target.value); setSearched(false); }} placeholder="Cliente, numero pratica, targa o numero sinistro" />
      </div><button type="submit" disabled={searching || !query.trim()}>{searching ? 'Ricerca…' : 'Cerca'}</button></form>
      {results.length > 0 && <div className="linked-search-results">{results.map(row => <article key={row.id}>
        <div><strong>{row.numero_progressivo} · {row.tipo}</strong><span>{clientName(row)}</span>
          <small>{[row.stato, row.data_sinistro && `Sinistro ${dateOnly(row.data_sinistro)}`, row.nome_compagnia].filter(Boolean).join(' · ')}</small></div>
        <button type="button" onClick={() => linkPractice(row.id)}><FaLink /> Collega</button>
      </article>)}</div>}
      {searched && results.length === 0 && <p className="linked-no-results">Nessuna pratica disponibile con questi criteri.</p>}
    </section>}

    <section className="linked-list-card">
      <div className="linked-list-heading"><div><span>COLLEGAMENTI ATTIVI</span><h2>Pratiche associate</h2></div><strong>{linked.length}</strong></div>
      {linked.length === 0 ? <div className="linked-empty"><FaLink /><strong>Nessuna pratica collegata</strong><span>Crea una nuova pratica dello stesso sinistro oppure collegane una esistente.</span></div>
        : <div className="linked-list">{linked.map(row => <article key={row.id}>
          <div className="linked-practice-info"><strong>{row.numero_progressivo} · {row.tipo}</strong><span>{clientName(row)}</span>
            <small>{[row.stato, row.data_sinistro && `Sinistro ${dateOnly(row.data_sinistro)}`].filter(Boolean).join(' · ')}</small>
            {row.percorso_clinico_condiviso && <em className="linked-medical-badge"><FaHeartbeat /> Percorso clinico condiviso</em>}</div>
          <div className="linked-practice-actions"><a href={`/pratica/${row.id}`} target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> Apri in nuova finestra</a>
            {!row.percorso_clinico_condiviso && <button type="button" className="share-medical" onClick={() => shareMedicalPath(row.id)}><FaHeartbeat /> Condividi percorso clinico</button>}
            <button type="button" aria-label={`Scollega pratica ${row.numero_progressivo}`} onClick={() => unlink(row.id)}><FaUnlink /> Scollega</button></div>
        </article>)}</div>}
    </section>
    {createOpen && <div className="linked-create-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && !creating && setCreateOpen(false)}>
      <form className="linked-create-modal" onSubmit={createLinkedPractice}>
        <header><div><span>STESSO SINISTRO</span><h2>Crea nuova pratica collegata</h2><p>Data, luogo, dinamica e tutti i veicoli saranno riportati nella nuova pratica.</p></div><button type="button" onClick={() => setCreateOpen(false)} disabled={creating}>×</button></header>
        {!createOptions ? <p className="linked-create-loading">Caricamento…</p> : <>
          <div className="linked-create-grid">
            <label>Tipologia della nuova pratica<select required value={createForm.tipo} onChange={event => changeTargetType(event.target.value)}><option value="">Seleziona la tipologia</option>{createOptions.practiceTypes.map(item => <option key={item}>{item}</option>)}</select></label>
            <label>Cliente della nuova pratica<select required value={createForm.cliente} onChange={event => {
              const cliente = event.target.value;
              const candidate = candidates.find(item => item.value === cliente);
              const sharesClient = normalizeName(candidate?.label) === normalizeName(createOptions.currentClient?.label);
              setCreateForm(previous => ({ ...previous, cliente, condividiSchedaMedica: sharesClient }));
            }}>{candidates.map((item, index) => <option key={`${item.value}-${index}`} value={item.value}>{item.label}{item.detail ? ` — ${item.detail}` : ' — Cliente attuale'}</option>)}</select></label>
          </div>
          {sameClient && <label className="linked-medical-toggle"><span><strong>Condividi percorso clinico</strong><small>Documenti, spese, prognosi e fine cure saranno unici per le due pratiche.</small></span><input type="checkbox" checked={createForm.condividiSchedaMedica} onChange={event => setCreateForm(previous => ({ ...previous, condividiSchedaMedica: event.target.checked }))} /><i aria-hidden="true" /></label>}
          <section className="linked-transfer-preview"><strong>Dati trasferiti</strong><p>Evento: data, luogo, dinamica, autorità e responsabilità.</p><p>Veicoli: {createOptions.vehicles.length} {createOptions.vehicles.length === 1 ? 'mezzo coinvolto' : 'mezzi coinvolti'}, completi dei dati già presenti.</p><small>Stato, trattative, visite, importi e scadenze operative partiranno autonomamente.</small></section>
        </>}
        <footer><button type="button" onClick={() => setCreateOpen(false)} disabled={creating}>Annulla</button><button type="submit" className="primary" disabled={creating || !createOptions || !createForm.tipo}>{creating ? 'Creazione…' : 'Crea e apri pratica'}</button></footer>
      </form>
    </div>}
    {medicalConflict && <div className="linked-create-overlay" role="presentation">
      <div className="linked-create-modal linked-medical-conflict" role="dialog" aria-modal="true">
        <header><div><span>PERCORSO CLINICO</span><h2>Quale scheda medica conserviamo?</h2><p>Entrambe le pratiche contengono già dati. Il percorso scelto sostituirà l'altro senza duplicare i documenti.</p></div><button type="button" onClick={() => setMedicalConflict(null)}>×</button></header>
        <div className="linked-conflict-choices"><button type="button" onClick={() => shareMedicalPath(medicalConflict.id, Number(schedaId))}><strong>Conserva questa pratica</strong><span>Usa il percorso clinico che stai visualizzando.</span></button><button type="button" onClick={() => shareMedicalPath(medicalConflict.id, Number(medicalConflict.id))}><strong>Conserva {medicalConflict.numero_progressivo || 'la pratica collegata'}</strong><span>Usa il percorso clinico della pratica collegata.</span></button></div>
        <footer><button type="button" onClick={() => setMedicalConflict(null)}>Annulla</button></footer>
      </div>
    </div>}
    {error && <p className="linked-error" role="alert">{error}</p>}
  </section>;
}
